import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddIcon from '@mui/icons-material/Add';
import useScrollReveal from '../../hooks/useScrollReveal';
import './index.css';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&display=swap';

// The display font is only needed on marketing pages, so load it on demand
const useDisplayFont = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// A button from page data: "#id" scrolls within the page, anything else is a route
export const Cta = ({ label, to, primary, onClick, className = '' }) => {
  const classes = `mkt-btn ${primary ? 'mkt-btn-primary' : 'mkt-btn-ghost'} ${className}`;
  const content = <>{label}{primary && <ArrowForwardIcon sx={{ fontSize: 19 }} />}</>;
  if (onClick) return <button type="button" className={classes} onClick={onClick}>{content}</button>;
  if (to.startsWith('#')) {
    return <a href={to} className={classes} onClick={(e) => { e.preventDefault(); scrollToId(to.slice(1)); }}>{content}</a>;
  }
  return <Link to={to} className={classes}>{content}</Link>;
};

// Page shell: accent colours, display font, scroll reveal and the sticky CTA bar
export const MarketingPage = ({ accent, sticky, children }) => {
  const ref = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  useDisplayFont();
  useScrollReveal(ref);

  // Show the sticky bar once the hero has scrolled away
  useEffect(() => {
    const hero = ref.current?.querySelector('.mkt-hero');
    if (!hero || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => setShowSticky(!entry.isIntersecting), { threshold: 0.05 });
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page mkt" ref={ref} style={{ '--a1': accent[0], '--a2': accent[1] }}>
      {children}
      {sticky && (
        <div className={`mkt-sticky ${showSticky ? 'is-visible' : ''}`} aria-hidden={!showSticky}>
          <span className="mkt-sticky-text"><span className="mkt-pulse" /> {sticky.text}</span>
          <Cta {...sticky.cta} primary className="mkt-sticky-btn" />
        </div>
      )}
    </div>
  );
};

export const Hero = ({ kicker, title, highlight, text, ctas, proof, visual }) => (
  <section className="mkt-hero">
    <div className="mkt-hero-bg" aria-hidden="true">
      <span className="mkt-orb mkt-orb-1" />
      <span className="mkt-orb mkt-orb-2" />
      <span className="mkt-grid" />
    </div>
    <div className="container mkt-hero-inner">
      <div className="mkt-hero-copy">
        <span className="mkt-kicker"><span className="mkt-pulse" /> {kicker}</span>
        <h1 className="mkt-display">
          {title} <span className="mkt-highlight">{highlight}</span>
        </h1>
        <p className="mkt-hero-text">{text}</p>
        <div className="mkt-ctas">
          {ctas.map((c) => <Cta key={c.label} {...c} />)}
        </div>
        {proof && (
          <ul className="mkt-proof">
            {proof.map((p) => <li key={p}><CheckRoundedIcon sx={{ fontSize: 16 }} /> {p}</li>)}
          </ul>
        )}
      </div>
      <div className="mkt-hero-visual">{visual}</div>
    </div>
  </section>
);

export const Ticker = ({ items }) => (
  <div className="mkt-ticker" aria-hidden="true">
    <Marquee speed={45} autoFill gradient={false}>
      {items.map((item) => (
        <span className="mkt-ticker-item" key={item}>{item}<i>✦</i></span>
      ))}
    </Marquee>
  </div>
);

export const SectionHead = ({ kicker, title, text, center }) => (
  <div className={`mkt-head ${center ? 'is-center' : ''}`} data-reveal>
    {kicker && <span className="mkt-eyebrow">{kicker}</span>}
    <h2 className="mkt-h2">{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

// "Same old story" crossed out, then the turn
export const Statement = ({ lead, struck, answer }) => (
  <section className="mkt-statement">
    <div className="container">
      <p className="mkt-statement-lead" data-reveal>{lead}</p>
      <ul className="mkt-struck">
        {struck.map((line, i) => (
          <li key={line} data-reveal style={{ '--i': i }}><s>{line}</s></li>
        ))}
      </ul>
      <p className="mkt-statement-answer" data-reveal style={{ '--i': struck.length }}>
        <span className="mkt-highlight">{answer}</span>
      </p>
    </div>
  </section>
);

export const BeforeAfter = ({ before, after }) => (
  <section className="mkt-section">
    <div className="container">
      <div className="mkt-ba">
        <div className="mkt-ba-col mkt-ba-before" data-reveal>
          <span className="mkt-ba-label">Before</span>
          <h3>{before.title}</h3>
          <ul>
            {before.items.map((item) => <li key={item}><CloseRoundedIcon sx={{ fontSize: 18 }} /> {item}</li>)}
          </ul>
        </div>
        <div className="mkt-ba-arrow" aria-hidden="true"><ArrowForwardIcon /></div>
        <div className="mkt-ba-col mkt-ba-after" data-reveal style={{ '--i': 1 }}>
          <span className="mkt-ba-label">After</span>
          <h3>{after.title}</h3>
          <ul>
            {after.items.map((item) => <li key={item}><CheckRoundedIcon sx={{ fontSize: 18 }} /> {item}</li>)}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export const FeatureRow = ({ kicker, title, text, bullets, visual, reverse, index }) => (
  <div className={`mkt-feature ${reverse ? 'is-reverse' : ''}`}>
    <div className="mkt-feature-copy" data-reveal>
      <span className="mkt-feature-num">{String(index + 1).padStart(2, '0')}</span>
      <span className="mkt-eyebrow">{kicker}</span>
      <h3 className="mkt-h3">{title}</h3>
      <p>{text}</p>
      {bullets && (
        <ul className="mkt-bullets">
          {bullets.map((b) => <li key={b}><CheckRoundedIcon sx={{ fontSize: 18 }} /> {b}</li>)}
        </ul>
      )}
    </div>
    <div className="mkt-feature-visual" data-reveal style={{ '--i': 1 }}>{visual}</div>
  </div>
);

export const Compare = ({ title, them, us, rows }) => (
  <section className="mkt-section">
    <div className="container">
      <SectionHead kicker="Why it's different" title={title} center />
      <div className="mkt-compare" data-reveal role="table" aria-label={title}>
        <div className="mkt-compare-row mkt-compare-head" role="row">
          <span role="columnheader" />
          <span role="columnheader">{them}</span>
          <span role="columnheader" className="is-us">{us}</span>
        </div>
        {rows.map(([label, a, b]) => (
          <div className="mkt-compare-row" role="row" key={label}>
            <span role="rowheader">{label}</span>
            <span role="cell" className="is-them"><CloseRoundedIcon sx={{ fontSize: 17 }} /> {a}</span>
            <span role="cell" className="is-us"><CheckRoundedIcon sx={{ fontSize: 17 }} /> {b}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Journey = ({ title, steps }) => (
  <section className="mkt-section">
    <div className="container">
      <SectionHead kicker="The plan" title={title} center />
      <ol className="mkt-journey">
        {steps.map((s, i) => (
          <li key={s.title} data-reveal style={{ '--i': i }}>
            <span className="mkt-journey-dot">{i + 1}</span>
            <small>{s.when}</small>
            <strong>{s.title}</strong>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const Faq = ({ items }) => (
  <section className="mkt-section">
    <div className="container mkt-faq-wrap">
      <SectionHead kicker="FAQ" title="Questions? Answered." />
      <div className="mkt-faqs" data-reveal style={{ '--i': 1 }}>
        {items.map((f) => (
          <details className="mkt-faq" key={f.q}>
            <summary>{f.q}<AddIcon className="mkt-faq-icon" /></summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCta = ({ title, text, ctas, children }) => (
  <section className="mkt-final">
    <div className="container">
      <div className="mkt-final-inner" data-reveal>
        <h2 className="mkt-display mkt-final-title">{title}</h2>
        <p>{text}</p>
        <div className="mkt-ctas is-center">
          {ctas.map((c) => <Cta key={c.label} {...c} />)}
        </div>
      </div>
      {children}
    </div>
  </section>
);
